import { Box, Flex, Skeleton, Text, Tooltip } from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useEffect } from 'react';

import { CHART_COLORS } from 'projectConstants';
import {
  fetchDimensionItems,
  getDimensionItems,
  getIndicatorDetailTableData,
} from 'redux/IndicatorPageSlice';
import { getCurrentDimensionIndex } from 'redux/InputPageSlice';
import {
  FetchDataInput,
  getBaseScenario,
  getDataInput,
  getExpandValue,
  getPlannerBlock,
  getPlannerModel,
  getSelectedIndicators,
  setExpand,
} from 'redux/PlannerModeSlice';
import ToggleIcons from 'components/Icons/ToggleIcons';

import ItemActions from './ItemActions';
import ExcelTable from './ExcelTable';
import UnExpandTable from './UnExpandTable';

interface Properties {
  refetchRequest: () => void;
}

const IndicatorTable = ({ refetchRequest }: Properties) => {
  const dispatch: any = useDispatch();
  const currentDriver = useSelector(getSelectedIndicators);
  const indicator: any = useSelector(getIndicatorDetailTableData);
  const dimensionItems = useSelector(getDimensionItems);
  const plannerModel = useSelector(getPlannerModel);
  const plannerBlock = useSelector(getPlannerBlock);
  const dataInputsValue: any = useSelector(getDataInput);
  const isExpand = useSelector(getExpandValue);
  const currentDimensionIndex = useSelector(getCurrentDimensionIndex);
  const baseScenario = useSelector(getBaseScenario);

  const { pathname } = useLocation();

  const commonObjects =
    plannerBlock?.dimensions?.length > 1
      ? plannerModel?.dimensions?.filter((object1: any) =>
          plannerBlock?.dimensions?.some(
            (object2: any) => object1.id === object2.id && object1.name !== 'Time',
          ),
        )
      : plannerModel?.dimensions?.filter((object1: any) =>
          plannerBlock?.dimensions?.some((object2: any) => object1.id === object2.id),
        );

  const dimensionId =
    currentDriver?.data_input?.dimensions[currentDimensionIndex] || commonObjects[0]?.id;
  const fetchDimItemsData = () => {
    dispatch(
      fetchDimensionItems({
        dimensionId,
        parameters: {
          scenario_id: baseScenario?.id,
        },
      }),
    );
  };
  useEffect(() => {
    if (currentDriver && currentDriver?.data_input?.dimensions?.length > 0) {
      fetchDimItemsData();
    }
  }, [pathname, currentDriver?.id, baseScenario?.id, currentDimensionIndex, dimensionId]);
  useEffect(() => {
    if (currentDriver) {
      fetchDimItemsData();
    }
  }, [pathname, dimensionId]);
  useEffect(() => {
    dispatch(
      FetchDataInput({
        indicator_id: currentDriver?.id,
        parameters: {
          scenario_id: baseScenario?.id,
          format_excel_data: true,
        },
      }),
    );
  }, [indicator?.input[0].type, plannerBlock?.dimensions?.length > 1, baseScenario]);
  useEffect(() => {
    dispatch(setExpand(dataInputsValue?.dimensions?.length > 0));
  }, [dataInputsValue?.dimensions]);

  if (!indicator) {
    return (
      <Flex direction='column' pb='1'>
        {[...new Array(6)].map((_, index) => (
          <Flex key={index} p={2} justifyContent={'space-between'}>
            {[...new Array(4)].map((__, subIndex: number) => (
              <Box key={subIndex} flex={1} mr={2}>
                <Skeleton startColor='white' endColor='#CCD4E8' height='35px' width={'100%'} />
              </Box>
            ))}
          </Flex>
        ))}
      </Flex>
    );
  }

  return (
    <>
      <Flex>
        {plannerBlock && plannerBlock?.dimensions?.length > 1 && (
          <Flex
            border={'2px solid rgba(255, 255, 255, 0.15)'}
            borderRadius={'7px'}
            p={1}
            w={'fit-content'}
            onClick={() => dispatch(setExpand(!isExpand))}
            bg={isExpand ? 'rgba(255, 255, 255, 0.15)' : 'transparent'}
          >
            <Tooltip label={'Expand view'}>
              <ToggleIcons />
            </Tooltip>
          </Flex>
        )}
      </Flex>
      {isExpand ? (
        <Flex>
          <Flex flexDirection={'column'} align={'center'} justify={'start'}>
            <Flex h={'25px'} w={'10px'}></Flex>
            {dimensionItems?.items?.map((row: any, index: number) => {
              return (
                <Flex key={index}>
                  <Flex px='1' pt={0}>
                    <Tooltip label={row?.name}>
                      <Flex alignItems={'center'}>
                        <Box bg={CHART_COLORS[index]} h='13px' w='13px' mr='2'></Box>
                        <ItemActions
                          row={row?.name}
                          currentDimensionItemIndex={index}
                          refetchRequest={refetchRequest}
                        />
                      </Flex>
                    </Tooltip>
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
          <ExcelTable
            currentDriver={currentDriver}
            commonObjects={commonObjects}
            dimensionItems={dimensionItems}
            isExpand={isExpand}
            plannerModel={plannerModel}
            refetchRequest={refetchRequest}
          />
        </Flex>
      ) : (
        <Flex>
          <Flex flexDirection={'column'} align={'center'} justify={'end'}>
            {commonObjects.map((row: any, index: number) => {
              return (
                <Flex key={index}>
                  <Flex px='1' py='1'>
                    <Tooltip label={row?.name}>
                      <Flex alignItems={'center'}>
                        <Box bg={CHART_COLORS[index]} h='15px' w='15px' mr='2'></Box>
                        <Tooltip label={row?.name}>
                          <Text fontSize={'xs'} noOfLines={1}>
                            {row?.name}
                          </Text>
                        </Tooltip>
                      </Flex>
                    </Tooltip>
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
          <UnExpandTable
            currentDriver={currentDriver}
            plannerModel={plannerModel}
            commonObjects={commonObjects}
            isExpand={isExpand}
            refetchRequest={refetchRequest}
          />
        </Flex>
      )}
    </>
  );
};

export default IndicatorTable;
